import { useCallback } from 'react'
import {
  clickCreateSideEffect,
  createHandleCancel,
  createHandleCreate,
  createHandleDelete,
  createHandleSaveEdit,
  createHandleSiteChange,
  createHandleSubmit,
  createHandleViewClass,
  createStateUpdater,
  createValidateForm,
  goBackOneModal,
  handleClassNameChange,
  handleDeleteSubmit,
  handleEditSubmit,
  handleError,
  handleGradeChange,
  handleResetFocus,
  resetState,
} from 'experienceComponents/CreateInstanceShared/sharedStateManagement.js'
import {
  courseFilterConfig,
  courseProgramConfig,
  dwbCourseFilterConfig,
  dwbCourseProgramConfig,
  k5CourseCentricProgramConfig,
  mspCourseCentricProgramConfig,
  courseViewClassConfig,
} from './courseConfig.js'
import { getCurrentProductFromUrl } from 'experienceComponents/CreateInstanceShared/dataUtils.js'
import { checkBlockingValidations } from './data'
import { isDigitalWellBeingPath } from 'utils/routing/routeHelpers'

export const getCourseRouteConfig = pathName => {
  if (isDigitalWellBeingPath(pathName)) {
    return {
      filterConfig: dwbCourseFilterConfig,
      programConfig: dwbCourseProgramConfig,
    }
  }

  const productName = getCurrentProductFromUrl(pathName)?.productName

  if (productName === 'elementary') {
    return {
      filterConfig: courseFilterConfig,
      programConfig: k5CourseCentricProgramConfig,
    }
  }

  if (productName === 'middle-school') {
    return {
      filterConfig: courseFilterConfig,
      programConfig: mspCourseCentricProgramConfig,
    }
  }

  return {
    filterConfig: courseFilterConfig,
    programConfig: courseProgramConfig,
  }
}

// Export the configured functions for CreateCourse
export const handleCreate = (state, props) => {
  const { filterConfig } = getCourseRouteConfig(props.pathName)
  return createHandleCreate(filterConfig)(state, props)
}

export const handleSubmit = async (state, props) => {
  const { programConfig } = getCourseRouteConfig(props.pathName)
  const handler = createHandleSubmit(
    programConfig,
    'Course was not created',
    'ERROR CREATING CLASS:',
  )
  return handler(state, props)
}

export const handleSaveEdit = createHandleSaveEdit('ERROR EDITING CLASS:')
export const handleViewClass = createHandleViewClass(courseViewClassConfig)
export const handleCancel = createHandleCancel()
export const handleSiteChange = createHandleSiteChange()
export const handleDelete = createHandleDelete('ERROR DELETING CLASS:')
export const validateForm = createValidateForm(checkBlockingValidations)

const applyStateResult = (result, updateState) => {
  updateState(result.newState, result.sideEffects)
}

const applyValidationResult = (result, updateState) => {
  updateState(result.newState)
}

// Custom hook that creates all callback handlers
export const useCreateCourseHandlers = (state, props, updateState) => {
  const resetStateCallback = useCallback(() => {
    applyStateResult(resetState(state, props), updateState)
  }, [state, props, updateState])

  const handleCreateCallback = useCallback(() => {
    applyStateResult(handleCreate(state, props), updateState)
  }, [state, props, updateState])

  const handleSubmitCallback = useCallback(async () => {
    const result = await handleSubmit(state, props)
    applyStateResult(result, updateState)
  }, [state, props, updateState])

  const goBackOneModalCallback = useCallback(() => {
    applyStateResult(goBackOneModal(state), updateState)
  }, [state, updateState])

  const handleClassNameChangeCallback = useCallback(
    e => {
      applyStateResult(handleClassNameChange(e, state), updateState)
    },
    [state, updateState],
  )

  const handleGradeChangeCallback = useCallback(
    (e, courses = []) => {
      applyStateResult(handleGradeChange(e, courses, state), updateState)
    },
    [state, updateState],
  )

  const handleSiteChangeCallback = useCallback(
    (e, sites = []) => {
      applyStateResult(handleSiteChange(e, sites, state, props), updateState)
    },
    [state, props, updateState],
  )

  const handleSaveEditCallback = useCallback(async () => {
    const result = await handleSaveEdit(state, props)
    applyStateResult(result, updateState)
  }, [state, props, updateState])

  const handleViewClassCallback = useCallback(() => {
    handleViewClass(state, props)
  }, [state, props])

  const handleCancelCallback = useCallback(() => {
    applyStateResult(handleCancel(state, props), updateState)
  }, [state, props, updateState])

  const handleDeleteSubmitCallback = useCallback(() => {
    applyStateResult(handleDeleteSubmit(state), updateState)
  }, [state, updateState])

  const handleEditSubmitCallback = useCallback(() => {
    applyStateResult(handleEditSubmit(state), updateState)
  }, [state, updateState])

  const handleDeleteCallback = useCallback(async () => {
    const result = await handleDelete(state, props)
    applyStateResult(result, updateState)
  }, [state, props, updateState])

  const validateFormCallback = useCallback(async () => {
    const result = await validateForm(state, props)
    applyValidationResult(result, updateState)
  }, [state, props, updateState])

  return {
    resetStateCallback,
    handleCreateCallback,
    handleSubmitCallback,
    goBackOneModalCallback,
    handleClassNameChangeCallback,
    handleGradeChangeCallback,
    handleSiteChangeCallback,
    handleSaveEditCallback,
    handleViewClassCallback,
    handleCancelCallback,
    handleDeleteSubmitCallback,
    handleEditSubmitCallback,
    handleDeleteCallback,
    validateFormCallback,
  }
}

// Export other functions that don't need configuration
export {
  createStateUpdater,
  handleResetFocus,
  resetState,
  clickCreateSideEffect,
  handleError,
  goBackOneModal,
  handleClassNameChange,
  handleGradeChange,
  handleDeleteSubmit,
  handleEditSubmit,
}
